import { AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';
import { INTEGRATION_TYPE_LABELS, SHIPPER_SCOPED_OVERRIDE_TYPES } from '../modals/constants';
import { evaluateDocStatus } from '../modals/DocStatusIcon';
import { MARKETPLACE_KEY } from '../utils';
import {
    CARRIER_STATUS,
    CARRIER_RELATIONSHIP_LABELS,
    COMPLIANCE_DOC_TYPES,
    LEG_LABELS_BY_ROLE,
    PRICING_LEG_BY_ROLE,
} from './constants';

const airportCodesFor = (client) => {
    const codes = [];
    for (const location of client?.locations || []) {
        const code = AIRPORT_CODES_BY_ZIP[location?.business_zip];
        if (code && !codes.includes(code)) codes.push(code);
    }
    return codes.sort();
};

const legLabelsFor = (client) => {
    const roles = client?.user?.roles || {};

    return Object.entries(LEG_LABELS_BY_ROLE).reduce((legLabels, [role, legLabel]) => {
        if (!roles[role]) return legLabels;

        legLabels.push(legLabel);

        return legLabels;
    }, []);
};

const carrierStatus = (client, compliance, isOwned) => {
    if (client?.deactivated) return CARRIER_STATUS.DEACTIVATED;
    const missingDocs = !compliance.every((doc) => doc.satisfied);
    // The org's own locations never file compliance docs.
    if (!isOwned && missingDocs) return CARRIER_STATUS.PENDING;
    return client?.approved ? CARRIER_STATUS.LIVE : CARRIER_STATUS.READY;
};

// Shipper-scoped types (PC Miler) are carrier-less, so they never count as a carrier's TMS.
const dispatchIntegrationsOf = (integrations) =>
    integrations.filter(
        (integration) =>
            integration?.type &&
            integration.type !== 'MANUAL' &&
            !SHIPPER_SCOPED_OVERRIDE_TYPES.includes(integration.type)
    );

// PENDING until the carrier's side is confirmed, by registration on save or by an engineer's Mark Complete.
const integrationStatusOf = (integrations) => {
    const dispatchIntegrations = dispatchIntegrationsOf(integrations);
    if (!dispatchIntegrations.length) return 'NOT STARTED';
    if (dispatchIntegrations.some((integration) => integration.status === 'ERROR')) return 'ERROR';
    if (dispatchIntegrations.every((integration) => integration.status === 'COMPLETE')) return 'CONNECTED';

    return 'PENDING';
};

const complianceStatusFor = (documents) => {
    const unexpiredDocTypes = documents.reduce((docTypes, doc) => {
        if (evaluateDocStatus(doc).state === 'expired') return docTypes;

        docTypes.add(doc.type);

        return docTypes;
    }, new Set());

    return COMPLIANCE_DOC_TYPES.map((type) => ({ type, satisfied: unexpiredDocTypes.has(type) }));
};

/** Sort comparator for clients by `business_name`, with a missing name sorting first. */
export const byBusinessName = (a, b) => (a.business_name || '').localeCompare(b.business_name || '');

const tmsTagsOf = (integrations) => {
    const tmsTags = dispatchIntegrationsOf(integrations).reduce((tags, { type }) => {
        if (tags.some((tag) => tag.type === type)) return tags;

        tags.push({ type, label: INTEGRATION_TYPE_LABELS[type] || type });

        return tags;
    }, []);
    // Sorted so the first, visible tag doesn't change with query order.
    tmsTags.sort((a, b) => a.label.localeCompare(b.label));

    return tmsTags;
};

export const buildCarrierRows = ({
    carriers,
    orgClientIds = [],
    contractorIds = [],
    integrationsByCarrier = {},
    documentsByCarrier = {},
}) => {
    const owned = new Set(orgClientIds);
    const contractors = new Set(contractorIds);

    const rows = carriers.reduce((carrierRows, carrier) => {
        const integrations = integrationsByCarrier[carrier.client_id] || [];
        const documents = documentsByCarrier[carrier.client_id] || [];
        const tmsTags = tmsTagsOf(integrations);
        const isContractor = contractors.has(carrier.client_id);
        // A contractor runs Final Mile whatever roles its account holds.
        const legs = isContractor ? [LEG_LABELS_BY_ROLE.CARRIER] : legLabelsFor(carrier);
        const compliance = complianceStatusFor(documents);
        const isOwned = owned.has(carrier.client_id);
        // Owned is checked last so it wins: Status and Active already treat the row as the org's.
        let relationship = 'PARTNER';
        if (isContractor) relationship = 'CONTRACTOR';
        if (isOwned) relationship = 'OWNED';

        carrierRows.push({
            client_id: carrier.client_id,
            carrier,
            business_name: carrier.business_name || '',
            airports: airportCodesFor(carrier),
            type: CARRIER_RELATIONSHIP_LABELS[relationship],
            isContractor,
            tmsTags,
            email: carrier.email || carrier.support_email || null,
            phone: carrier.business_phone || carrier.support_phone || null,
            legs,
            status: carrierStatus(carrier, compliance, isOwned),
            owned: isOwned,
            active: !carrier.deactivated && !!carrier.approved,
            // approved is platform-wide, so only the org's own accounts can be switched here.
            canToggleActive: isOwned && !carrier.deactivated,
            compliance,
            integrations,
            documents,
        });

        return carrierRows;
    }, []);

    return rows.sort(byBusinessName);
};

/** True for the org's own location; an owned account that is also a contractor shows as a Motor Carrier instead. */
export const isFlexCenterRow = (row) => row.owned && !row.isContractor;

export const isFulfillmentPartnerRow = (row) => !row.owned && !row.isContractor && !row.isMarketplace;

export const signsInOnOpen = (row) => row.owned || isFulfillmentPartnerRow(row);

const EMPTY_SHIPPER_SETTINGS = { integrations: [], carrierPricing: [], shipperPricing: [] };

export const pricingSummaryOf = (overrides, tariffsById) => {
    const tariffNames = [];
    const pricingKeys = [];
    for (const override of overrides) {
        // Disconnecting a carrier soft-deletes its rates as enabled: false.
        if (!override.enabled) continue;

        // A rate with no tariff is bespoke to its shipper, so it keys on its own id.
        pricingKeys.push(override.tariff_id || override.pricing_override_id);
        const tariffName = tariffsById[override.tariff_id]?.name;
        if (tariffName) tariffNames.push(tariffName);
    }
    pricingKeys.sort();

    return { tariffNames, pricingKeys };
};

export const defaultPricingLegsFor = (row) => {
    if (row.isContractor) return ['CONTRACTOR'];

    const roles = row.carrier?.user?.roles || {};

    return Object.entries(PRICING_LEG_BY_ROLE).reduce((legs, [role, leg]) => {
        if (!roles[role]) return legs;

        legs.push(leg);

        return legs;
    }, []);
};

export const shipperSettingsFor = ({
    carrierId,
    linkedShippers = [],
    settingsByShipper = {},
    assignmentsByShipper = {},
    addendumsByShipper = {},
    capacityShipperIds = new Set(),
    tariffsById = {},
    clientsById = {},
    defaultIntegrations = [],
}) => {
    const hasConfiguredDefault = dispatchIntegrationsOf(defaultIntegrations).length > 0;
    const linkedOrConfiguredShipperIds = new Set([
        ...linkedShippers.map((shipper) => shipper.client_id),
        ...Object.keys(settingsByShipper),
    ]);

    return [...linkedOrConfiguredShipperIds].map((shipperId) => {
        const settings = settingsByShipper[shipperId] || EMPTY_SHIPPER_SETTINGS;
        const profiles = assignmentsByShipper[shipperId] || [];
        // Sorted so the same set of profiles groups together whatever order the query returns.
        const profileNames = profiles.reduce((names, profile) => {
            if (names.includes(profile.profileName)) return names;

            names.push(profile.profileName);

            return names;
        }, []);
        profileNames.sort();
        // Carrier pay only: it is all the default covers, so shipper charges must not displace it.
        const { tariffNames, pricingKeys } = pricingSummaryOf(settings.carrierPricing, tariffsById);
        const addendum = addendumsByShipper[shipperId];
        // Must agree with onward-functions' orgDefaultFor: a row of the shipper's own, even MANUAL, replaces it.
        // Only on the dispatch leg; a middle-mile row on this carrier leaves the default running.
        const hasOwnDispatchRow = settings.integrations.some((integration) => integration.carrier_id === carrierId);
        const ridesDefaultIntegration = hasConfiguredDefault && !hasOwnDispatchRow;
        const appliedIntegrations = ridesDefaultIntegration ? defaultIntegrations : settings.integrations;

        return {
            client_id: shipperId,
            business_name: clientsById[shipperId]?.business_name || shipperId,
            settings,
            tmsTags: tmsTagsOf(appliedIntegrations),
            integrationStatus: integrationStatusOf(appliedIntegrations),
            ridesDefaultIntegration,
            hasCapacity: capacityShipperIds.has(shipperId),
            tariffNames,
            pricingKeys,
            profileNames,
            onMap: profiles.some((profile) => profile.mappedCarrierIds.has(carrierId)),
            rateAddendum: addendum && !addendum.archived ? addendum : null,
        };
    });
};

const settingsKeyOf = (shipper) =>
    JSON.stringify([
        shipper.tmsTags.map((tag) => tag.type),
        shipper.integrationStatus,
        shipper.ridesDefaultIntegration,
        shipper.hasCapacity,
        shipper.pricingKeys,
        shipper.profileNames,
        shipper.onMap,
        !!shipper.rateAddendum,
    ]);

export const pricingDetailOf = ({ tariffNames, pricingKeys }) =>
    tariffNames.join(', ') || `${pricingKeys.length} custom rate(s)`;

export const NO_DEFAULT_PRICING = { overrides: [], tariffNames: [], pricingKeys: [] };

export const defaultPricingCardFor = (defaultPricing) => {
    const hasDefault = defaultPricing.pricingKeys.length > 0;

    return {
        key: 'default_pricing',
        title: 'Pricing',
        detail: hasDefault ? `Default: ${pricingDetailOf(defaultPricing)}` : 'No default tariff',
        statusLabel: hasDefault ? 'APPLIED' : 'NONE',
        satisfied: hasDefault,
        actionLabel: hasDefault ? 'Update' : 'Set default',
    };
};

export const flexCentersFor = ({ flexCenters = [], pricingByFlexCenter = {}, tariffsById = {}, defaultPricing }) =>
    flexCenters.map((flexCenter) => {
        const carrierPricing = pricingByFlexCenter[flexCenter.client_id] || [];
        const ownPricing = pricingSummaryOf(carrierPricing, tariffsById);
        const hasOwnPricing = ownPricing.pricingKeys.length > 0;
        const hasDefault = defaultPricing.pricingKeys.length > 0;
        const isPriced = hasOwnPricing || hasDefault;

        let detail = 'No pricing';
        if (hasDefault) detail = `Default: ${pricingDetailOf(defaultPricing)}`;
        if (hasOwnPricing) detail = pricingDetailOf(ownPricing);

        return {
            client_id: flexCenter.client_id,
            business_name: flexCenter.business_name || flexCenter.client_id,
            settings: { integrations: [], carrierPricing, shipperPricing: [] },
            pricingCard: {
                key: 'pricing',
                title: 'Pricing',
                detail,
                statusLabel: isPriced ? 'APPLIED' : 'NONE',
                satisfied: isPriced,
                actionLabel: isPriced ? 'Update' : 'Set pricing',
            },
        };
    });

export const MARKETPLACE_ROW = {
    client_id: MARKETPLACE_KEY,
    carrier: null,
    business_name: 'Onward Marketplace',
    airports: [],
    type: null,
    isContractor: false,
    isMarketplace: true,
    tmsTags: [],
    email: null,
    phone: null,
    legs: [],
    status: null,
    owned: false,
    active: false,
    canToggleActive: false,
    compliance: [],
    integrations: [],
    documents: [],
};

// Marketplace pricing is the shipper's own charge (no carrier), so its card reads shipperPricing.
export const marketplaceShippersFor = ({ settingsByShipper, assignmentsByShipper, tariffsById, clientsById }) => {
    const shippers = shipperSettingsFor({
        carrierId: MARKETPLACE_KEY,
        settingsByShipper,
        assignmentsByShipper,
        tariffsById,
        clientsById,
    });

    return shippers.map((shipper) => {
        const shipperPricing = pricingSummaryOf(shipper.settings.shipperPricing, tariffsById);
        const isPriced = shipperPricing.pricingKeys.length > 0;
        const profileNames = shipper.profileNames.join(', ');
        const pricingCard = {
            key: 'pricing',
            title: 'Pricing',
            detail: isPriced ? pricingDetailOf(shipperPricing) : 'No pricing',
            statusLabel: isPriced ? 'APPLIED' : 'NONE',
            satisfied: isPriced,
            actionLabel: isPriced ? 'Update' : 'Set pricing',
        };
        const profileCard = {
            key: 'delivery_profile',
            title: 'Delivery Profile',
            detail: profileNames || 'No profile applied',
            statusLabel: profileNames ? 'APPLIED' : 'NONE',
            satisfied: !!profileNames,
            actionLabel: 'Map',
        };

        return { ...shipper, cards: [pricingCard, profileCard] };
    });
};

export const defaultIntegrationCardFor = (defaultIntegrations) => {
    const tmsLabelList = tmsTagsOf(defaultIntegrations).map((tag) => tag.label);
    const tmsLabels = tmsLabelList.join(', ');
    const integrationStatus = integrationStatusOf(defaultIntegrations);

    return {
        key: 'integration',
        title: 'Integrations',
        detail: tmsLabels ? `Default: ${tmsLabels}` : 'No default integration',
        detailItems: tmsLabelList,
        detailLead: 'Default:',
        statusLabel: integrationStatus,
        satisfied: integrationStatus === 'CONNECTED',
        pending: integrationStatus === 'PENDING',
        actionLabel: tmsLabels ? 'Update' : 'Set default',
    };
};

/**
 * The Delivery Profile card for a shipper: its profile names and whether that profile is on the map.
 * @param {{ profileNames: string[], onMap: boolean }} representative
 */
export const deliveryProfileCardFor = (representative) => {
    const profileNames = representative.profileNames.join(', ');
    let profileStatus = 'NONE';
    if (profileNames) profileStatus = representative.onMap ? 'APPLIED' : 'NOT ON MAP';

    return {
        key: 'delivery_profile',
        title: 'Delivery Profile',
        detail: profileNames || 'No profile applied',
        statusLabel: profileStatus,
        satisfied: profileStatus === 'APPLIED',
        actionLabel: 'Map',
    };
};

const settingCardsFor = (shippers, defaultPricing) => {
    const [representative] = shippers;
    const tmsLabelList = representative.tmsTags.map((tag) => tag.label);
    const tmsLabels = tmsLabelList.join(', ');
    let integrationDetail = tmsLabels || 'None configured';
    if (representative.ridesDefaultIntegration) integrationDetail = `Default: ${tmsLabels}`;
    if (representative.hasCapacity) integrationDetail = `${integrationDetail} · Capacity`;
    // Stays a per-shipper card on the default, so its modal can add the shipper's own rate.
    const hasOwnPricing = representative.pricingKeys.length > 0;
    let pricingDetail = `Default: ${pricingDetailOf(defaultPricing)}`;
    if (hasOwnPricing) pricingDetail = pricingDetailOf(representative);
    let pricingCard = defaultPricingCardFor(defaultPricing);
    if (hasOwnPricing || defaultPricing.pricingKeys.length > 0) {
        pricingCard = {
            key: 'pricing',
            title: 'Pricing',
            detail: pricingDetail,
            statusLabel: 'APPLIED',
            satisfied: true,
            actionLabel: 'Update',
        };
    }
    const signedCount = shippers.reduce((count, shipper) => (shipper.rateAddendum ? count + 1 : count), 0);
    const allSigned = signedCount === shippers.length;

    return [
        {
            key: 'integration',
            title: 'Integrations',
            detail: integrationDetail,
            detailItems: tmsLabelList,
            detailLead: representative.ridesDefaultIntegration ? 'Default:' : null,
            detailTrail: representative.hasCapacity ? '· Capacity' : null,
            statusLabel: representative.integrationStatus,
            satisfied: representative.integrationStatus === 'CONNECTED',
            pending: representative.integrationStatus === 'PENDING',
            actionLabel: 'Send / configure',
        },
        pricingCard,
        deliveryProfileCardFor(representative),
        {
            key: 'rate_addendum',
            title: 'Rate Addendum',
            detail: `${signedCount} of ${shippers.length} per-shipper signed`,
            statusLabel: allSigned ? 'SIGNED' : 'NOT SIGNED',
            satisfied: allSigned,
            actionLabel: 'Upload',
        },
    ];
};

export const groupShippersBySettings = (shippers = [], { defaultPricing = NO_DEFAULT_PRICING } = {}) => {
    const shippersByKey = new Map();
    for (const shipper of shippers) {
        const key = settingsKeyOf(shipper);
        if (!shippersByKey.has(key)) shippersByKey.set(key, []);
        shippersByKey.get(key).push(shipper);
    }

    const groups = [...shippersByKey.entries()].map(([key, groupShippers]) => {
        groupShippers.sort(byBusinessName);
        const cards = settingCardsFor(groupShippers, defaultPricing);

        return { key, shippers: groupShippers, cards };
    });

    // Largest first: it is the carrier's common setup, and the rest are exceptions.
    return groups.sort((a, b) => b.shippers.length - a.shippers.length || byBusinessName(a.shippers[0], b.shippers[0]));
};

export const flattenCarrierAssetMaps = (assetMaps) => {
    const byCarrierId = new Map();

    for (const assetMap of assetMaps) {
        for (const [carrierId, entry] of Object.entries(assetMap)) {
            // The marketplace stands in for "no carrier"; it is not an account.
            if (carrierId === MARKETPLACE_KEY) continue;
            const carrier = entry.carrier;
            if (!carrier?.client_id) continue;

            const { carrier: _carrier, ...settingsByShipper } = entry;
            const integrations = Object.values(settingsByShipper).flatMap((settings) => settings?.integrations || []);

            const existing = byCarrierId.get(carrierId);
            if (!existing) {
                byCarrierId.set(carrierId, { carrier, integrations, settingsByShipper });
                continue;
            }

            existing.integrations.push(...integrations);
            Object.assign(existing.settingsByShipper, settingsByShipper);
        }
    }

    return [...byCarrierId.values()];
};

// A carrier the org was ever rated or integrated with stays listed, even once its pricing is disabled.
export const accountCarrierEntries = ({ carrierEntries = [], contractors = [], defaultCarriers = [] }) => {
    const listedIds = new Set(carrierEntries.map((entry) => entry.carrier.client_id));
    const accountEntries = [...carrierEntries];

    for (const { client_id: contractorId, contractor } of contractors) {
        if (!contractor || listedIds.has(contractorId)) continue;

        listedIds.add(contractorId);
        accountEntries.push({ carrier: contractor, integrations: [], settingsByShipper: {} });
    }

    // A default outlives the per-shipper rows that would otherwise list its carrier.
    for (const carrier of defaultCarriers) {
        if (listedIds.has(carrier.client_id)) continue;

        listedIds.add(carrier.client_id);
        accountEntries.push({ carrier, integrations: [], settingsByShipper: {} });
    }

    return accountEntries;
};

// Linked: a profile auto-assigned to the shipper ranks the carrier in one of its subregions.
export const linkedShippersByCarrierFrom = (assignmentsByShipper = {}, clientsById = {}) => {
    const byCarrier = {};

    for (const [shipperId, profiles] of Object.entries(assignmentsByShipper || {})) {
        for (const profile of profiles) {
            for (const carrierId of profile.mappedCarrierIds) {
                byCarrier[carrierId] ??= [];
                if (byCarrier[carrierId].some((shipper) => shipper.client_id === shipperId)) continue;
                byCarrier[carrierId].push({
                    client_id: shipperId,
                    business_name: clientsById[shipperId]?.business_name || shipperId,
                });
            }
        }
    }

    return byCarrier;
};

const rowMatches = (row, query) =>
    [row.business_name, row.email, row.carrier?.dot_number, row.client_id, ...row.airports].some(
        (value) => value && value.toString().toLowerCase().includes(query)
    );

// A contractor has no row of its own, so finding one shows the Flex Center it sits under.
export const filterCarrierRows = (rows, search, contractorsByFlexCenter = {}) => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
        if (rowMatches(row, query)) return true;

        const contractors = contractorsByFlexCenter[row.client_id] || [];

        return contractors.some(({ contractorRow }) => rowMatches(contractorRow, query));
    });
};
